import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  Close,
  Save,
  AccountBalanceWallet,
  CreditCard,
  Payment,
  Receipt,
  QrCode,
  VpnKey,
  AccountBalance,
  Store,
  AccountBalanceOutlined,
} from '@mui/icons-material';
import formaPgtoService, { FormaPgto } from '../../services/formaPgtoService';

interface FormaPgtoFormProps {
  open: boolean;
  onClose: () => void;
  formaPgto?: FormaPgto | null;
  onSuccess?: (formaPgto: FormaPgto) => void;
  onError?: (error: string) => void;
}

// Mapeamento de ícones por nome
const iconMap: { [key: string]: React.ReactElement } = {
  'AccountBalanceWallet': <AccountBalanceWallet />,
  'CreditCard': <CreditCard />,
  'Payment': <Payment />,
  'Receipt': <Receipt />,
  'QrCode': <QrCode />,
  'VpnKey': <VpnKey />,
  'AccountBalance': <AccountBalance />,
  'AccountBalanceOutlined': <AccountBalanceOutlined />,
  'Store': <Store />,
};

const iconOptions = [
  { value: 'AccountBalanceWallet', label: 'Dinheiro', icon: <AccountBalanceWallet /> },
  { value: 'CreditCard', label: 'Cartão de Crédito', icon: <CreditCard /> },
  { value: 'Payment', label: 'Cartão de Débito', icon: <Payment /> },
  { value: 'Receipt', label: 'Cheque', icon: <Receipt /> },
  { value: 'QrCode', label: 'PIX Maquininha', icon: <QrCode /> },
  { value: 'VpnKey', label: 'Chave PIX', icon: <VpnKey /> },
  { value: 'AccountBalanceOutlined', label: 'Banco/Boleto', icon: <AccountBalanceOutlined /> },
  { value: 'AccountBalance', label: 'Depósito/Transferência', icon: <AccountBalance /> },
  { value: 'Store', label: 'Gateway de Cobrança', icon: <Store /> },
];

const FormaPgtoForm: React.FC<FormaPgtoFormProps> = ({
  open,
  onClose,
  formaPgto,
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    icone: 'Payment',
    ativo: true,
    ordem: 0
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Resetar formulário quando modal abrir/fechar
  useEffect(() => {
    if (open) {
      if (formaPgto) {
        setFormData({
          codigo: formaPgto.codigo,
          nome: formaPgto.nome,
          descricao: formaPgto.descricao || '',
          icone: formaPgto.icone || 'Payment',
          ativo: formaPgto.ativo,
          ordem: formaPgto.ordem
        });
      } else {
        setFormData({
          codigo: '',
          nome: '',
          descricao: '',
          icone: 'Payment',
          ativo: true,
          ordem: 0
        });
      }
      setErrors([]);
    }
  }, [open, formaPgto]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.codigo.trim()) {
      newErrors.push('Código é obrigatório');
    } else if (!/^[a-z_]+$/.test(formData.codigo)) {
      newErrors.push('Código deve conter apenas letras minúsculas e underscore');
    }

    if (!formData.nome.trim()) {
      newErrors.push('Nome é obrigatório');
    }

    if (formData.descricao && formData.descricao.length > 255) {
      newErrors.push('Descrição deve ter no máximo 255 caracteres');
    }

    if (formData.icone && formData.icone.length > 100) {
      newErrors.push('Ícone deve ter no máximo 100 caracteres');
    }

    if (formData.ordem < 0) {
      newErrors.push('Ordem deve ser um número não negativo');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (formaPgto) {
        // Atualizar forma de pagamento existente
        const updatedFormaPgto = await formaPgtoService.updateForma(formaPgto.id, formData);
        onSuccess?.(updatedFormaPgto);
      } else {
        // Criar nova forma de pagamento
        const newFormaPgto = await formaPgtoService.createForma(formData);
        onSuccess?.(newFormaPgto);
      }

      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar forma de pagamento:', error);
      onError?.(error.message || 'Erro ao salvar forma de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {formaPgto ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento'}
          </Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Código *"
              value={formData.codigo}
              onChange={(e) => handleInputChange('codigo', e.target.value)}
              placeholder="Ex: dinheiro, cartao_credito"
              disabled={loading}
              helperText="Apenas letras minúsculas e underscore"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome *"
              value={formData.nome}
              onChange={(e) => handleInputChange('nome', e.target.value)}
              placeholder="Ex: Dinheiro, Cartão de Crédito"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              placeholder="Descrição detalhada da forma de pagamento"
              multiline
              rows={2}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ícone</InputLabel>
              <Select
                value={formData.icone}
                onChange={(e) => handleInputChange('icone', e.target.value)}
                label="Ícone"
                disabled={loading}
                renderValue={(selected) => {
                  const option = iconOptions.find(opt => opt.value === selected);
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      {option?.icon}
                      <Typography>{option?.label}</Typography>
                    </Box>
                  );
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ordem"
              type="number"
              value={formData.ordem}
              onChange={(e) => handleInputChange('ordem', parseInt(e.target.value) || 0)}
              disabled={loading}
              helperText="Ordem de exibição no select"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={(e) => handleInputChange('ativo', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Ativo"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Quando ativo, a forma de pagamento estará disponível para seleção
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<Save />}
          disabled={loading}
        >
          {loading ? 'Salvando...' : (formaPgto ? 'Atualizar' : 'Criar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormaPgtoForm;
